import { config } from '../config';
import { API_ENDPOINTS } from '../constants/endpoints';
import { ChargebeeResponse, DownloadInvoiceResponse, InvoiceResponse, ChangePlanByEmailRequest, PaymentMethodStatusResponse } from '../types/chargebee';
import { get, post } from '../utils/api.utils';
import { decodeIdToken, decodeMetadataToken } from '../utils/token.utils';
import AuthService from './auth.service';
import { validateAndSanitizeName, sanitizeTextInput } from '../utils/common.util';

declare const Chargebee: any;
class ChargebeeService {
  public static initChargebee(site: string) {
    try {
    const cbInstance = Chargebee?.init({
        site: site,
        publishableKey: config.chargebee_publishableKey // todo - check key and dynamic
      });
      cbInstance.setPortalSession(() => {
        // https://apidocs.chargebee.com/docs/api/portal_sessions#create_a_portal_session

        const session = this.getPortalSession()
        return session
          
      });
      console.log('Chargebee initialized successfully');
    } catch (error) {
      console.error('Failed to initialize Chargebee:', error);
    }
  }

  public static async getCustomerInvoices(customerId: string): Promise<InvoiceResponse> {
    try {
      const response = await get<InvoiceResponse>(
        API_ENDPOINTS.SUBSCRIPTIONS.GET_CUSTOMER_INVOICES(customerId)
      );
      
      return response;
    } catch (error: any) {
      if (error.response?.status === 404) {
        throw new Error('Customer not found');
      }
      if (error.response?.status === 401) {
        throw new Error('Unauthorized access');
      }
      
      throw new Error('Failed to fetch customer invoices');
    }
  }

  public static async downloadInvoice(invoiceId: string): Promise<Blob> {
    try {
      const response = await get<DownloadInvoiceResponse>(
        API_ENDPOINTS.SUBSCRIPTIONS.DOWNLOAD_INVOICE(invoiceId)
      );
      
      const pdfResponse = await fetch(response.data.download_url);
      if (!pdfResponse.ok) {
        throw new Error('Failed to download invoice PDF');
      }
      
      return await pdfResponse.blob();
    } catch (error: any) {
      if (error.response?.status === 404) {
        throw new Error('Invoice not found');
      }
      if (error.response?.status === 401) {
        throw new Error('Unauthorized access');
      }
      
      throw new Error('Failed to download invoice');
    }
  }

  public static async getPortalSession(): Promise<ChargebeeResponse> {
    try {
      const metadata = decodeMetadataToken();
      const customerId = metadata?.companies?.[0]?.id
      
      if (!customerId) {
        throw new Error('Customer ID not found in token');
      }

      const response = await get<ChargebeeResponse>(
        API_ENDPOINTS.SUBSCRIPTIONS.GET_PORTAL_SESSION(customerId)
      );

      if (!response.data?.id) {
        throw new Error('Invalid portal session response');
      }
      
      return response.data;
    } catch (error: any) {
      console.error('Detailed error:', error);
      throw error;
    }
  }

  public static async openBillingSection(): Promise<void> {
    try {
      var chargebeeInstance = Chargebee.getInstance();
      let cbPortal = chargebeeInstance.createChargebeePortal();

      const callbacks = {
        close: () => {
          console.log('Billing section closed');
        },
        loaded: () => {
          console.log('Billing section loaded');
        },
        error: (error: any) => {
          console.error('Portal error:', error);
        }
      }

      cbPortal.openSection({
        sectionType: Chargebee.getPortalSections().ADDRESS,
      }, callbacks)
    } catch (error) {
      console.error('Failed to open billing section:', error);
      throw error;
    }
  }

  public static async openPaymentSection(onPaymentSourceAdded?: () => Promise<void>): Promise<void> {
    try {
      var chargebeeInstance = Chargebee.getInstance();
      let cbPortal = chargebeeInstance.createChargebeePortal();
      const callbacks = {
        close: () => {
          console.log('Payment section closed');
        },
        loaded: () => {
          console.log('Payment section loaded');
        },
        success: () => {
          console.log('Payment section success');
        },
        error: (error: any) => {
          console.error('Portal error:', error);
        },
        paymentSourceAdd: async () => {
          console.log('Payment source added');
          try {
            // Get customer ID from metadata
            const metadata = decodeMetadataToken();
            const customerId = metadata?.companies?.[0]?.id;
            
            if (customerId) {
              // Fetch updated subscription details
              const subscriptionDetails = await this.getSubscriptionDetails(customerId);
              console.log('Updated subscription details:', subscriptionDetails);

              const subscriptionId = subscriptionDetails.data[0].subscription.id;
              const planUnits = subscriptionDetails.data[0].subscription_items[0].quantity;
              const planId = subscriptionDetails.data[0].subscription_items[0].itemPriceId;

              if(planUnits === 10) {
                let newPlanUnits = 10;

                if(planId.includes('Live-Webinars')) {
                  newPlanUnits = 50;
                }
                else if(planId.includes('Automated-Webinars')) {
                  newPlanUnits = 200;
                }
                else if(planId.includes('All-In-One')) {
                  newPlanUnits = 250;
                }
              
                await this.changePlan(subscriptionId, planId, newPlanUnits);
              }
              
              // Execute custom callback if provided
              if (onPaymentSourceAdded) {
                await onPaymentSourceAdded();
              }
              
              return subscriptionDetails;
            }
          } catch (error) {
            console.error('Failed to fetch subscription details after adding payment source:', error);
          }
        }
      }

      cbPortal.openSection({
        sectionType: Chargebee.getPortalSections().PAYMENT_SOURCES,
      }, callbacks)

    } catch (error) {
      console.error('Failed to open payment section:', error);
      throw error;
    }
  }

  public static async getSubscriptionDetails(customerId: string): Promise<any> {
    try {
      const response = await get<any>(
        API_ENDPOINTS.SUBSCRIPTIONS.GET_SUBSCRIPTION_DETAILS(customerId)
      );
      
      return response;
    } catch (error: any) {
      if (error.response?.status === 404) {
        throw new Error('Subscription not found');
      }
      if (error.response?.status === 401) {
        throw new Error('Unauthorized access');
      }
      
      throw new Error('Failed to fetch subscription details');
    }
  }

  private static transformCouponCode(couponId?: string): string | undefined {
    if (!couponId) return couponId;
    
    switch (couponId.toUpperCase()) {
      case 'EW50':
        return 'EW_50';
      case 'EW30':
        return 'EW-30';
      default:
        return couponId;
    }
  }

  public static async upgradePlanUsingCheckout(subscriptionId: string, planId: string, units?: number, trialEnd?: number, couponId?: string): Promise<any> {
    try {
      const transformedCouponId = this.transformCouponCode(couponId);
      
      const response = await post<any>(
        API_ENDPOINTS.SUBSCRIPTIONS.UPGRADE_PLAN,
        {
          subscriptionId,
          planId,
          units,
          trialEnd,
          couponId: transformedCouponId
        }
      );
      
      // Call refresh token if upgrade was successful
      if (response.status === 200) {
        await AuthService.refreshToken();
      }
      
      return response.data;
    } catch (error: any) {
      if (error.response?.status === 404) {
        throw new Error('Subscription not found');
      }
      if (error.response?.status === 401) {
        throw new Error('Unauthorized access');
      }
      
      throw new Error('Failed to upgrade subscription plan');
    }
  }

  public static async getPlans(): Promise<any> {
    try {
      const response = await get<any>(
        API_ENDPOINTS.SUBSCRIPTIONS.GET_PLANS
      );
      
      return response;
    } catch (error: any) {
      if (error.response?.status === 404) {
        throw new Error('Plans not found');
      }
      if (error.response?.status === 401) {
        throw new Error('Unauthorized access');
      }
      
      throw new Error('Failed to fetch plans');
    }
  }

  public static async getPlanById(planId: string): Promise<any> {
    try {
      const response = await get<any>(
        API_ENDPOINTS.SUBSCRIPTIONS.GET_PLAN_BY_ID(planId)
      );
      
      return response;
    } catch (error: any) {
      if (error.response?.status === 404) {
        throw new Error('Plan not found');
      }
      if (error.response?.status === 401) {
        throw new Error('Unauthorized access');
      }
      
      throw new Error('Failed to fetch plan details');
    }
  }

  public static async validateCoupon(couponCode: string, planId?: string): Promise<any> {
    try {
        const transformedCouponCode = this.transformCouponCode(couponCode);
        
        const endpoint = planId 
            ? API_ENDPOINTS.SUBSCRIPTIONS.VALIDATE_COUPON(transformedCouponCode!, planId)
            : API_ENDPOINTS.SUBSCRIPTIONS.VALIDATE_COUPON(transformedCouponCode!);
            
        const response = await get<any>(endpoint);
        return response;
    } catch (error: any) {
        if (error.response?.status === 404) {
            throw new Error('Coupon not found');
        }
        if (error.response?.status === 401) {
            throw new Error('Unauthorized access');
        }
        
        throw new Error('Failed to validate coupon');
    }
  }

  public static async addPaymentSource(customerId: string, cardToken: string): Promise<any> {
    try {
      const response = await post<any>(
        API_ENDPOINTS.SUBSCRIPTIONS.ADD_PAYMENT_SOURCE,
        {
          customerId,
          cardToken
        }
      );
      
      return response.data;
    } catch (error: any) {
      if (error.response?.status === 404) {
        throw new Error('Customer not found');
      }
      if (error.response?.status === 400) {
        throw new Error('Invalid payment information');
      }
      
      throw new Error('Failed to add payment source');
    }
  }

  public static async createSubscription(customerId: string, planId: string, couponId?: string, planUnits?: number, trialEnd?: number): Promise<any> {
    try {
      const transformedCouponId = this.transformCouponCode(couponId);
      
      const payload = {
        customerId,
        planId,
        ...(transformedCouponId && { couponId: transformedCouponId }),
        ...(planUnits && { planUnits }),
        ...(trialEnd && { trialEnd })
      };
      
      const response = await post<any>(
        API_ENDPOINTS.SUBSCRIPTIONS.CREATE_SUBSCRIPTION,
        payload
      );
      
      return response.data;
    } catch (error: any) {
      if(error.response?.status === 409) {
        throw new Error(error.response?.status);
      }
      if (error.response?.status === 404) {
        throw new Error('Customer or plan not found');
      }
      if (error.response?.status === 401) {
        throw new Error('Unauthorized access');
      }
      if (error.response?.status === 400) {
        throw new Error('Invalid subscription information');
      }
      
      throw new Error('Failed to create subscription');
    }
  }

  public static async changePlan(subscriptionId: string, planId: string, units: number, billingCycle?: string): Promise<any> {
    try {
      const response = await post<any>(
        API_ENDPOINTS.SUBSCRIPTIONS.CHANGE_PLAN,
        {
          subscriptionId,
          planId,
          units,
          ...(billingCycle && { billingCycle })
        }
      );
      
      // Call refresh token if plan change was successful
      if (response.status === 200) {
        await AuthService.refreshToken();
      }
      
      return response.data;
    } catch (error: any) {
      if (error.response?.status === 404) {
        throw new Error('Subscription or plan not found');
      }
      if (error.response?.status === 401) {
        throw new Error('Unauthorized access');
      }
      if (error.response?.status === 400) {
        throw new Error('Invalid plan change information');
      }
      
      throw new Error('Failed to change subscription plan');
    }
  }

  public static async getBillingDetails(customerId: string): Promise<ChargebeeResponse> {
    try {
      const response = await get<ChargebeeResponse>(
        API_ENDPOINTS.SUBSCRIPTIONS.GET_BILLING_DETAILS(customerId)
      );
      
      return response;
    } catch (error: any) {
      if (error.response?.status === 404) {
        throw new Error('Customer billing details not found');
      }
      if (error.response?.status === 401) {
        throw new Error('Unauthorized access');
      }
      
      throw new Error('Failed to fetch billing details');
    }
  }

  public static async cancelSubscription(subscriptionId: string): Promise<ChargebeeResponse> {
    try {
      const response = await post<any>(
        API_ENDPOINTS.SUBSCRIPTIONS.CANCEL_SUBSCRIPTION(subscriptionId)
      );
      
      return response.data;
    } catch (error: any) {
      if (error.response?.status === 404) {
        throw new Error('Subscription not found');
      }
      if (error.response?.status === 401) {
        throw new Error('Unauthorized access');
      }
      if (error.response?.status === 400) {
        throw new Error('Invalid cancellation request');
      }
      
      throw new Error('Failed to cancel subscription');
    }
  }

  public static async openEditSubscriptionSection(subscriptionId: string): Promise<void> {
    try {
      const chargebeeInstance = Chargebee.getInstance();
      const cbPortal = chargebeeInstance.createChargebeePortal();
      const callbacks = {
        close: async () => {
          console.log('Edit subscription section closed');
          // Refresh token when the edit subscription section is closed
          try {
            await AuthService.refreshToken();
            console.log('Token refreshed after subscription edit');
          } catch (refreshError) {
            console.error('Failed to refresh token after subscription edit:', refreshError);
          }
        },
        loaded: () => {
          console.log('Edit subscription section loaded');
        },
        error: (error: any) => {
          console.error('Portal error:', error);
        }
      };
      
      cbPortal.open(callbacks, {
        sectionType: Chargebee.getPortalSections().EDIT_SUBSCRIPTION,
        params: {
          subscriptionId: subscriptionId
        }
      });
    } catch (error) {
      console.error('Failed to open edit subscription section:', error);
      throw error;
    }
  }

  public static async chargeCustomer(data: {
    firstName: string;
    lastName: string;
    email: string;
    itemPriceId: string;
    cardToken: string;
  }): Promise<any> {
    try {
      // Security: Validate and sanitize input data to prevent hyperlink injection
      const firstNameValidation = validateAndSanitizeName(data.firstName, 'First name');
      if (!firstNameValidation.isValid) {
        throw new Error(firstNameValidation.error || 'Invalid first name provided');
      }
      
      const lastNameValidation = validateAndSanitizeName(data.lastName || '', 'Last name', false);
      if (!lastNameValidation.isValid) {
        throw new Error(lastNameValidation.error || 'Invalid last name provided');
      }
      
      // Create sanitized data object
      const sanitizedData = {
        firstName: firstNameValidation.sanitizedValue || '',
        lastName: lastNameValidation.sanitizedValue || '',
        email: data.email?.trim().toLowerCase() || '',
        itemPriceId: sanitizeTextInput(data.itemPriceId),
        cardToken: data.cardToken // Card token should not be sanitized as it's encrypted
      };
      
      const response = await post<any>(
        API_ENDPOINTS.SUBSCRIPTIONS.CUSTOMER_CHARGE,
        sanitizedData
      );
      
      return response;
    } catch (error: any) {
      if (error.response?.status === 400) {
        throw new Error('Invalid request data');
      }
      if (error.response?.status === 401) {
        throw new Error('Unauthorized access');
      }
      if (error.response?.status === 404) {
        throw new Error('Customer or item not found');
      }
      if (error.response?.status === 500) {
        throw new Error('Internal server error');
      }
      
      // Re-throw validation errors
      if (error.message && (error.message.includes('contains') || error.message.includes('Invalid'))) {
        throw error;
      }
      
      throw new Error('Failed to charge customer');
    }
  }

  public static async quickCharge(customerId: string, itemPriceId: string, quantity: number = 1): Promise<any> {
    try {
      const response = await post<any>(
        API_ENDPOINTS.SUBSCRIPTIONS.QUICK_CHARGE,
        {
          customerId,
          itemPriceId,
          quantity
        }
      );
      
      return response.data;
    } catch (error: any) {
      if (error.response?.status === 400) {
        throw new Error('Invalid request data');
      }
      if (error.response?.status === 401) {
        throw new Error('Unauthorized access');
      }
      if (error.response?.status === 404) {
        throw new Error('Customer or item not found');
      }
      if (error.response?.status === 500) {
        throw new Error('Internal server error');
      }
      
      throw new Error('Failed to quick charge customer');
    }
  }

  public static async changePlanByEmail(data: ChangePlanByEmailRequest): Promise<any> {
    try {
      const payload = {
        email: data.email,
        cardToken: data.cardToken,
        planId: data.planId,
        planUnits: data.planUnits,
        ...(data.couponId && { couponId: data.couponId })
      };
      
      const response = await post<any>(
        API_ENDPOINTS.SUBSCRIPTIONS.CHANGE_PLAN_BY_EMAIL,
        payload
      );
      
      return response.data;
    } catch (error: any) {
      if (error.response?.status === 400) {
        throw new Error('Invalid request data');
      }
      if (error.response?.status === 401) {
        throw new Error('Unauthorized access');
      }
      if (error.response?.status === 404) {
        throw new Error('Customer or plan not found');
      }
      if (error.response?.status === 500) {
        throw new Error('Internal server error');
      }
      
      throw new Error('Failed to change plan by email');
    }
  }

  public static async getPaymentMethodStatus(customerId: string): Promise<PaymentMethodStatusResponse> {
    try {
      const response = await get<PaymentMethodStatusResponse>(
        API_ENDPOINTS.SUBSCRIPTIONS.GET_PAYMENT_METHOD_STATUS(customerId)
      );
      
      return response;
    } catch (error: any) {
      if (error.response?.status === 404) {
        throw new Error('Customer not found');
      }
      if (error.response?.status === 401) {
        throw new Error('Unauthorized access');
      }
      
      throw new Error('Failed to fetch payment method status');
    }
  }
}

export default ChargebeeService;
